import { Lightbulb, X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@higgins/ui';
import { useOutdoor, useSunTips } from '../lib/outdoor';

/** What helps most in sun, beyond Outdoor's colours (#707). docs/USERS.md "Working in bright sun" says the same. */
const SUN_TIPS = [
  'Turn screen brightness to full: swipe down from the top-right corner, then drag the brightness slider up.',
  'A matte (anti-glare) screen protector cuts reflection more than any colour setting. Usually available at Officeworks or JB Hi-Fi; get one for your iPad model (Settings → General → About shows the model).',
  'Tilt the iPad away from the sun, or stand so your body shades the screen.',
  "For even stronger contrast, turn on the iPad's own Increase Contrast (Settings → Accessibility → Display & Text Size).",
];

/** The "Working in bright sun" card: opens the first time Outdoor goes on, and from Sun tips after that. */
function SunTipsCard() {
  const [open, setOpen] = useSunTips();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* The shared dialog's own small × is hidden: this card has 44px ones. */}
      <DialogContent className="max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg text-slate-900 sm:max-w-lg [&>button:last-child]:hidden">
        <div className="flex items-start justify-between gap-3">
          <DialogTitle className="pt-2.5">Working in bright sun</DialogTitle>
          <DialogClose aria-label="Close" className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-700">
            <X className="h-5 w-5" />
          </DialogClose>
        </div>
        <DialogDescription className="text-sm text-slate-700">Outdoor makes the screen easier to read. These help even more:</DialogDescription>
        <ol className="list-decimal space-y-3 pl-5 text-base text-slate-800">
          {SUN_TIPS.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ol>
        <div className="flex justify-end">
          <DialogClose className="min-h-11 rounded-md bg-slate-900 px-6 text-base font-medium text-white">Close</DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The header's Sun tips button, shown only while Outdoor is on, with the card it opens. */
export function SunTips() {
  const [outdoor] = useOutdoor();
  const [, setOpen] = useSunTips();
  return (
    <>
      {outdoor && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Tips for working in bright sun"
          aria-label="Sun tips"
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white text-sm font-medium text-slate-700 lg:px-3"
        >
          <Lightbulb className="h-4 w-4" aria-hidden="true" />
          {/* Words only from 1024px wide: in portrait they squeeze the app's name. */}
          <span className="hidden lg:inline">Sun tips</span>
        </button>
      )}
      <SunTipsCard />
    </>
  );
}
